import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { AlertTriangle, ExternalLink } from "lucide-react";

// Colour + copy for each quota bucket returned by /api/cloudflare-stream/quota.
// Only "critical" (>=80%) and "exceeded" (>=100%) render the banner — "warning"
// (60-79%) is deliberately silent to avoid banner fatigue; operators still see
// the number if they open the Cloudflare dashboard.
// Tailwind class strings are kept STATIC (not template-interpolated) so the
// production purge keeps them alive.
const BUCKETS = {
  critical: {
    bg: "bg-amber-500/15 border-b border-amber-500/40",
    dot: "bg-amber-400 animate-pulse",
    iconClass: "text-amber-400",
    label: "CDN STORAGE 80%+ FULL",
  },
  exceeded: {
    bg: "bg-rose-500/20 border-b border-rose-500/60",
    dot: "bg-rose-500 animate-pulse",
    iconClass: "text-rose-400",
    label: "CDN STORAGE FULL — UPLOADS WILL FAIL",
  },
};

const CF_TOPUP_URL = "https://dash.cloudflare.com/?to=/:account/stream";

/**
 * Proactive banner that warns operators when the Cloudflare Stream
 * storage-minutes quota is close to running out. Polls /quota every
 * 5 minutes (backend cache is also 5 min, so this stays cheap even
 * with many console tabs open).
 *
 * Renders nothing when:
 *   - Cloudflare Stream isn't configured (`configured: false`)
 *   - The quota lookup errored (`ok: false`)
 *   - Usage is below 80% (`warning_level ∈ {ok, warning}`)
 *   - The user dismissed it (in-memory only — a page refresh brings it
 *     back so ops sees fresh state after any topup gap)
 */
export default function CloudflareQuotaBanner() {
  const [quota, setQuota] = useState(null);
  const [retired, setRetired] = useState(null);  // null = unknown until /r2/status resolves
  // In-memory dismissal ONLY. sessionStorage would persist across reload
  // (defeating the purpose of a proactive warning). Tracked per bucket so
  // an escalation from critical → exceeded re-triggers the banner.
  const [dismissedBuckets, setDismissedBuckets] = useState(() => new Set());

  const fetchQuota = useCallback(async () => {
    try {
      const r = await api.get("/cloudflare-stream/quota");
      setQuota(r.data);
    } catch (_) {
      setQuota(null);
    }
  }, []);

  // Cloudflare Stream is retired once storage lives on R2 — hide this banner.
  useEffect(() => {
    api.get("/r2/status").then((r) => setRetired(!!r.data?.enabled)).catch(() => setRetired(false));
  }, []);

  useEffect(() => {
    if (retired !== false) return undefined;
    fetchQuota();
    const id = setInterval(fetchQuota, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [fetchQuota, retired]);

  if (retired !== false) return null;
  if (!quota || !quota.configured || !quota.ok) return null;
  const level = quota.warning_level;
  if (level !== "critical" && level !== "exceeded") return null;
  if (dismissedBuckets.has(level)) return null;

  const meta = BUCKETS[level];
  // Two-decimal precision — .toFixed(1) rounds 85.99 → 86.0 which
  // slightly overstates usage; 2dp matches the API response.
  const pct = Number(quota.percent_used || 0).toFixed(2);
  const free = quota.minutes_free != null
    ? `${Math.round(quota.minutes_free)} min free`
    : "quota exhausted";

  return (
    <div
      data-testid={`cf-quota-banner-${level}`}
      className={`px-4 py-2 flex items-center justify-between gap-3 text-xs backdrop-blur-md ${meta.bg}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className={`inline-block w-2 h-2 rounded-full shrink-0 ${meta.dot}`} />
        <AlertTriangle className={`w-4 h-4 shrink-0 ${meta.iconClass}`} />
        <span
          data-testid="cf-quota-banner-label"
          className="font-mono text-[11px] uppercase tracking-widest text-foreground shrink-0"
        >
          {meta.label}
        </span>
        <span
          data-testid="cf-quota-banner-detail"
          className="text-muted-foreground truncate"
        >
          <b className="text-foreground">{pct}%</b> of {Math.round(quota.minutes_limit)} minutes used
          {" · "}{free}
          {" · "}<b className="text-foreground">{quota.videos}</b> videos
          {level === "exceeded" ? " · new uploads WILL 413 until you top up." : " · new uploads will fail soon."}
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <a
          href={CF_TOPUP_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="cf-quota-topup-btn"
          className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest px-3 py-1 rounded-sm bg-foreground text-background hover:opacity-90"
        >
          Top up <ExternalLink className="w-3 h-3" />
        </a>
        <button
          onClick={() =>
            setDismissedBuckets((prev) => new Set(prev).add(level))
          }
          data-testid="cf-quota-dismiss-btn"
          className="text-[11px] font-mono uppercase tracking-widest px-3 py-1 rounded-sm hover:bg-foreground/10"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
